/**
 * Cart Milestones dynamic updater and smooth transition handler
 */

if (typeof window.lastCartTotal === 'undefined') {
  window.lastCartTotal = null;
}

/**
 * Formats a number in Indian currency format (e.g. ₹1,999 or ₹499.50)
 * @param {number} amount - The currency amount.
 * @returns {string} The formatted currency string.
 */
function formatIndianCurrency(amount) {
  const hasDecimals = amount % 1 !== 0;
  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'decimal',
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2
  });
  return '₹' + formatter.format(amount);
}

/**
 * Updates a milestone bar element's DOM based on the new cart total.
 * @param {HTMLElement} bar - The cart milestone bar container element.
 * @param {number} cartTotal - The new cart total in major currency.
 */
function updateMilestoneBar(bar, cartTotal) {
  const milestonesJson = bar.getAttribute('data-milestones');
  if (!milestonesJson) return;

  let milestones = [];
  try {
    milestones = JSON.parse(milestonesJson);
  } catch (e) {
    console.error('Error parsing milestones JSON:', e);
    return;
  }

  // Sort milestones ascending by amount
  milestones.sort((a, b) => a.amount - b.amount);
  const count = milestones.length;
  if (count === 0) return;

  const maxAmount = milestones[count - 1].amount;
  const messageContainer = bar.querySelector('.cart-milestones__status');
  const progressFill = bar.querySelector('.cart-milestones__bar');
  const markers = bar.querySelectorAll('.cart-milestones__marker');

  // Edge Case 1: Cart is Empty
  if (cartTotal === 0) {
    const firstMilestone = milestones[0];
    if (firstMilestone && messageContainer) {
      messageContainer.innerHTML = `Add <strong class="cart-milestones__amount">${formatIndianCurrency(firstMilestone.amount)}</strong> to start unlocking rewards!`;
    }
    if (progressFill) {
      progressFill.style.width = '0%';
    }
    markers.forEach(marker => {
      marker.classList.remove('is-unlocked');
    });
    bar.setAttribute('data-cart-total', '0');
    bar.setAttribute('data-all-unlocked', 'false');
    return;
  }

  // Recalculate which milestones are unlocked
  const unlockedStates = milestones.map(m => cartTotal >= m.amount);
  const allUnlocked = unlockedStates.every(state => state === true);

  // Update container attributes
  bar.setAttribute('data-cart-total', cartTotal);
  bar.setAttribute('data-all-unlocked', allUnlocked ? 'true' : 'false');

  // Edge Case 2: All Milestones Unlocked
  if (allUnlocked) {
    if (messageContainer) {
      messageContainer.innerHTML = `<span class="cart-milestones__celebration">🎉 All rewards unlocked!</span>`;
    }
    if (progressFill) {
      progressFill.style.width = '100%';
    }
  } else {
    // Normal progress case
    const nextIndex = unlockedStates.indexOf(false);
    const nextMilestone = milestones[nextIndex];
    if (nextMilestone) {
      const remainingAmount = Math.max(0, nextMilestone.amount - cartTotal);
      if (messageContainer) {
        messageContainer.innerHTML = `Add <strong class="cart-milestones__amount">${formatIndianCurrency(remainingAmount)}</strong> more to unlock <strong>${nextMilestone.reward}</strong>`;
      }
    }
    
    // Update progress bar fill
    const overallProgress = maxAmount > 0 ? Math.min((cartTotal / maxAmount) * 100, 100) : 0;
    if (progressFill) {
      progressFill.style.width = `${overallProgress}%`;
    }
  }

  // Update marker states (active classes triggers transition in CSS)
  markers.forEach(marker => {
    const markerAmount = parseFloat(marker.getAttribute('data-marker-amount'));
    const isUnlocked = cartTotal >= markerAmount;

    if (isUnlocked) {
      marker.classList.add('is-unlocked');
    } else {
      marker.classList.remove('is-unlocked');
    }
  });
}

/**
 * Initializes/updates the milestone bar smoothly on DOM insertions or updates.
 */
function initMilestones() {
  const bar = document.querySelector('.cart-milestones');
  if (!bar) return;

  const currentTotal = parseFloat(bar.getAttribute('data-cart-total') || '0');

  // If we have a stored previous total and it changed, animate smoothly
  if (window.lastCartTotal !== null && window.lastCartTotal !== currentTotal) {
    const milestonesJson = bar.getAttribute('data-milestones');
    if (milestonesJson) {
      try {
        const milestones = JSON.parse(milestonesJson).sort((a, b) => a.amount - b.amount);
        const maxAmount = milestones[milestones.length - 1]?.amount || 0;
        if (maxAmount > 0) {
          const prevProgress = Math.min((window.lastCartTotal / maxAmount) * 100, 100);
          const currentProgress = Math.min((currentTotal / maxAmount) * 100, 100);

          const progressFill = bar.querySelector('.cart-milestones__bar');
          if (progressFill) {
            // Instantly jump to previous progress position
            progressFill.style.transition = 'none';
            progressFill.style.width = `${prevProgress}%`;

            // Force reflow
            progressFill.offsetHeight;

            // Restore transition and slide to new progress
            progressFill.style.transition = '';
            progressFill.style.width = `${currentProgress}%`;
          }
        }
      } catch (e) {
        console.error('Error during milestones transition:', e);
      }
    }
  }

  // Update last known total
  window.lastCartTotal = currentTotal;
}

// Initial initialization
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMilestones);
} else {
  initMilestones();
}

// Listen for custom cart:update events (fired by Shopify Ajax)
document.addEventListener('cart:update', async (event) => {
  // Let the cart complete its network/render request before we sync the latest
  setTimeout(async () => {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const cartTotal = cart.total_price / 100;

      const milestoneBars = document.querySelectorAll('.cart-milestones');
      milestoneBars.forEach((bar) => {
        updateMilestoneBar(bar, cartTotal);
      });
      
      window.lastCartTotal = cartTotal;
    } catch (err) {
      console.error('Failed to sync milestones on cart update:', err);
    }
  }, 50);
});

// Watch for theme morph events that insert/replace the DOM
const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    if (mutation.type === 'childList') {
      const bar = document.querySelector('.cart-milestones');
      if (bar) {
        initMilestones();
        break;
      }
    }
  }
});

observer.observe(document.body, { childList: true, subtree: true });

// Delegate clicks on claimable freebie buttons
document.addEventListener('click', async (event) => {
  const button = event.target.closest('.cart-milestones__freebie-btn');
  if (!button || button.disabled || button.classList.contains('is-claimed')) return;

  const variantId = button.getAttribute('data-variant-id');
  if (!variantId) return;

  button.disabled = true;
  const originalText = button.textContent;
  button.textContent = 'Claiming...';

  // Gather sections to render/morph
  const sectionsToUpdate = new Set();
  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  cartItemsComponents.forEach((item) => {
    if (item.dataset.sectionId) {
      sectionsToUpdate.add(item.dataset.sectionId);
    }
  });

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items: [{
          id: parseInt(variantId, 10),
          quantity: 1
        }],
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname
      })
    });

    if (response.ok) {
      const responseData = await response.json();

      // Fetch the latest cart count to update the header icon bubble
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();

      // Dispatch cart:update to trigger theme morph and dynamic re-rendering
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          data: {
            itemCount: cartData.item_count,
            sections: responseData.sections
          }
        }
      }));
    } else {
      const errData = await response.json();
      console.error('Failed to claim freebie:', errData.description || errData.message);
      button.disabled = false;
      button.textContent = originalText;
      alert('Could not claim gift: ' + (errData.description || errData.message));
    }
  } catch (error) {
    console.error('Error claiming freebie:', error);
    button.disabled = false;
    button.textContent = originalText;
  }
});
